<script setup lang="ts">
/**
 * 菜品卡片。
 *
 * 触屏上的三个细节：
 *   1. 整张卡片都是可点区域（不只是那个小小的加号）—— 手指没那么准；
 *   2. 售罄的菜**不隐藏**，而是灰掉并盖一层「已售罄」：凭空消失会让顾客反复找；
 *   3. 价格用后端给的 priceText，前端不重新格式化，避免和后端算出来的不一致。
 */
import type { MenuProduct } from '../api/types';

const props = defineProps<{ product: MenuProduct; quantity: number }>();
const emit = defineEmits<{ (e: 'pick', product: MenuProduct): void }>();

function onClick(): void {
  if (props.product.isSoldOut) return;
  emit('pick', props.product);
}
</script>

<template>
  <button
    class="card"
    :class="{ 'card--sold-out': product.isSoldOut }"
    :disabled="product.isSoldOut"
    @click="onClick"
  >
    <div class="card__thumb">
      <img v-if="product.imageUrl" :src="product.imageUrl" :alt="product.name" />
      <span v-else class="card__thumb-fallback">🍚</span>
      <span v-if="product.isSoldOut" class="card__sold-out">已售罄</span>
    </div>
    <div class="card__body">
      <div class="card__name">{{ product.name }}</div>
      <div class="card__desc">{{ product.description || '　' }}</div>
      <div class="card__foot">
        <span class="amount card__price">¥{{ product.priceText }}</span>
        <span v-if="quantity > 0" class="card__badge">{{ quantity }}</span>
        <span v-else class="card__add">＋</span>
      </div>
    </div>
  </button>
</template>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
  text-align: left;
  background: var(--panel);
  border: 2px solid var(--line);
  border-radius: var(--radius);
  overflow: hidden;
  min-height: 250px;
  transition: transform 0.06s ease, border-color 0.12s ease;
}

.card:active {
  transform: scale(0.985);
  border-color: var(--brand);
}

.card--sold-out {
  opacity: 0.5;
}

.card__thumb {
  position: relative;
  height: 116px;
  background: var(--panel-2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card__thumb-fallback {
  font-size: 46px;
  opacity: 0.5;
}

.card__sold-out {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 12, 14, 0.66);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 2px;
}

.card__body {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.card__name {
  font-size: 21px;
  font-weight: 700;
  line-height: 1.25;
}

.card__desc {
  font-size: 14px;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card__foot {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card__price {
  font-size: 24px;
  color: #ffd08a;
}

.card__add {
  margin-left: auto;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: var(--brand);
  color: #fff;
  font-size: 30px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.card__badge {
  margin-left: auto;
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-weight: 800;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 860px) {
  /* 手机上一屏要看到尽量多的菜：两列、卡片变矮、描述让位给菜名和价格 */
  .card {
    min-height: 0;
    border-width: 1px;
  }

  .card__thumb {
    height: 84px;
  }

  .card__thumb-fallback {
    font-size: 34px;
  }

  .card__body {
    padding: 8px 10px 10px;
    gap: 4px;
  }

  .card__name {
    font-size: 16px;
    /* 菜名最多两行，超出省略 —— 宁可截断也不要撑破卡片 */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card__desc {
    display: none;
  }

  .card__price {
    font-size: 18px;
  }

  .card__add {
    width: 38px;
    height: 38px;
    font-size: 24px;
  }

  .card__badge {
    min-width: 32px;
    height: 32px;
    font-size: 16px;
  }
}

</style>
